import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const Auth = z.object({ id: z.string().uuid(), token: z.string().min(20).max(100) });

export const startChat = createServerFn({ method: "POST" })
  .validator((d: unknown) =>
    z.object({
      name: z.string().trim().min(2).max(80),
      phone: z.string().trim().regex(/^[+0-9 ()-]{9,20}$/),
    }).parse(d),
  )
  .handler(async ({ data }) => {
    const { adminDb } = await import("./symptom-chat.server");
    const db = await adminDb();
    const first = data.name.split(" ")[0];
    const greeting = {
      role: "assistant",
      content: `Thanks ${first}! So, what's the problem? Tell me where the pain or issue is and how long you've had it.`,
      at: new Date().toISOString(),
    };
    const { data: row, error } = await db
      .from("symptom_chats")
      .insert({ patient_name: data.name, patient_phone: data.phone, messages: [greeting] })
      .select("id, access_token")
      .single();
    if (error) { console.error("[symptom-chat] start", error.message); throw new Error("Could not start chat"); }
    return { id: row.id as string, token: row.access_token as string, messages: [greeting] };
  });

export const sendChatMessage = createServerFn({ method: "POST" })
  .validator((d: unknown) => Auth.extend({ text: z.string().trim().min(1).max(2000) }).parse(d))
  .handler(async ({ data }) => {
    const { loadChat, askModel, adminDb } = await import("./symptom-chat.server");
    const chat = await loadChat(data.id, data.token);
    if (!chat) return { ok: false as const, error: "Chat not found. Please start again." };
    if (chat.messages.length > 80) return { ok: false as const, error: "This chat is quite long — please continue on WhatsApp." };
    const history = [...chat.messages, { role: "user" as const, content: data.text, at: new Date().toISOString() }];
    const db = await adminDb();
    let reply: string;
    try {
      reply = await askModel(chat.patient_name, history);
    } catch (e) {
      await db.from("symptom_chats").update({ messages: history, updated_at: new Date().toISOString() }).eq("id", chat.id);
      const msg = e instanceof Error && e.message === "rate_limited" ? "Lots of people are chatting right now. Please try again in a minute." : "Sorry, something went wrong. Please try again or chat to us on WhatsApp.";
      return { ok: false as const, error: msg };
    }
    const messages = [...history, { role: "assistant" as const, content: reply, at: new Date().toISOString() }];
    await db.from("symptom_chats").update({ messages, updated_at: new Date().toISOString(), emailed_at: null }).eq("id", chat.id);
    return { ok: true as const, messages };
  });

export const closeChat = createServerFn({ method: "POST" })
  .validator((d: unknown) => Auth.extend({ whatsapp: z.boolean().optional() }).parse(d))
  .handler(async ({ data }) => {
    const { loadChat, adminDb, emailTranscript } = await import("./symptom-chat.server");
    const chat = await loadChat(data.id, data.token);
    if (!chat) return { ok: false };
    const db = await adminDb();
    await db.from("symptom_chats").update({ closed_at: new Date().toISOString(), ...(data.whatsapp ? { whatsapp_clicked: true } : {}) }).eq("id", chat.id);
    await emailTranscript(chat.id);
    return { ok: true };
  });

export const listChats = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await (context.supabase as any)
      .from("symptom_chats")
      .select("id, patient_name, patient_phone, messages, whatsapp_clicked, created_at, updated_at, closed_at, emailed_at")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return data as { id: string; patient_name: string; patient_phone: string; messages: { role: string; content: string; at: string }[]; whatsapp_clicked: boolean; created_at: string; updated_at: string; closed_at: string | null; emailed_at: string | null }[];
  });
